import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import {
  MultipartResumePayload,
  MultipartS3UploadService,
} from "../../../../shared/services/multipart-s3-upload.service";
import { VideoThumbnailService } from "../../../../shared/services/video-thumbnail.service";
import { UploadObsService } from "../../../../shared/services/upload-obs.service";
import { HeicConverterService } from "../../../../shared/services/heic-converter.service";
import Swal from "sweetalert2";
import { TravelKitService } from "../../../../shared/services/travel-kit.service";
import { persistExistingVideoThumbnail } from "../travel-kit-modify/shared/persist-video-thumbnail.util";

export interface UploadProgress {
  fileId: string;
  percent: number;
  status: "pending" | "uploading" | "completed" | "error" | "paused";
  uploadedParts?: number;
  totalParts?: number;
}

export interface UploadState {
  fileId: string;
  fileName: string;
  file: File;
  uploadId: string;
  key: string;
  partsCount: number;
  uploadedParts: Array<{ Etag: string; PartNumber: number }>;
  presignedUrls: string[];
  currentPartIndex: number;
  sectionIndex: number;
  subIndex: number;
  mediaItem: any;
  folder: string;
  fileType: string;
  resumeCount?: number; // Track number of resume attempts (max 3)
}

@Component({
  selector: "app-multi-upload",
  templateUrl: "./multi-upload.component.html",
  styleUrls: ["./multi-upload.component.scss"],
})
export class MultiUploadComponent implements OnInit, OnDestroy {
  private readonly AUTO_RETRY_MAX_ATTEMPTS = 3;
  private readonly AUTO_RETRY_DELAY_MS = 1200;
  @Input() sectionIndex: number;
  @Input() subIndex: number;
  @Input() subsection: any;
  @Input() sectionVideoCount: number | null = null;
  @Input() uploadIdPrefix: string = "videoUpload";
  @Input() folder: string = "digital/videos";
  @Input() maxSizeMB: number = 5120; // 5GB default
  @Input() chunkSizeMB: number = 15; // 15MB per chunk
  @Input() maxVideosPerSection: number = 15; // Max videos per section

  @Output() uploadComplete = new EventEmitter<{
    file: File;
    url: string;
    fileId: string;
    mediaItem: any;
  }>();
  @Output() uploadError = new EventEmitter<{
    file: File;
    error: string;
    fileId: string;
  }>();
  @Output() uploadProgress = new EventEmitter<UploadProgress>();
  @Output() deleteMediaEvent = new EventEmitter<number>();
  @Output() modelChange = new EventEmitter<any>();

  uploadStates: Map<string, UploadState> = new Map();
  uploadProgressMap: Map<string, UploadProgress> = new Map();
  private readonly STORAGE_PREFIX = "multipart_upload_";
  private uploadCancelled: Map<string, boolean> = new Map(); // Track cancelled uploads
  private uploadStreamSubs: Map<string, Subscription> = new Map();
  private isDestroying: boolean = false; // Flag to track if component is being destroyed
  private thumbnailUploadStates: { [key: string]: boolean } = {};
  private thumbnailUploadProgress: { [key: string]: number } = {};
  private thumbnailProgressTimers: { [key: string]: any } = {};
  private thumbnailProgressStartedAt: { [key: string]: number } = {};
  private visibilityChangeHandler: (() => void) | null = null;
  private windowFocusHandler: (() => void) | null = null;
  private pendingAutoThumbnailJobs: Array<() => Promise<void>> = [];
  private runningAutoThumbnailJobs = 0;
  /** Avoid duplicate URL thumbnail requests; key from thumbnailFromUrlDedupeKey */
  private thumbnailFromUrlInFlight = new Set<string>();

  constructor(
    private multipartS3Upload: MultipartS3UploadService,
    private toastr: ToastrService,
    private videoThumbnailService: VideoThumbnailService,
    private uploadService: UploadObsService,
    private heicConverter: HeicConverterService,
    private travelKitService: TravelKitService
  ) {}

  ngOnInit(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === "visible") {
        this.retryMissingVideoThumbnails();
      }
    };
    this.windowFocusHandler = () => this.retryMissingVideoThumbnails();
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);
    window.addEventListener("focus", this.windowFocusHandler);
    this.migrateBase64ThumbnailsToS3();
  }

  /**
   * After upload, `tempId` is removed; thumbnail callbacks must still find the row.
   */
  private findMediaIndexByClientKey(mediaItem: any): number {
    if (!mediaItem || !this.subsection.media) {
      return -1;
    }
    if (mediaItem.mediaClientKey) {
      const byKey = this.subsection.media.findIndex((m: any) => m.mediaClientKey === mediaItem.mediaClientKey);
      if (byKey !== -1) {
        return byKey;
      }
    }
    if (mediaItem.tempId) {
      return this.subsection.media.findIndex((m: any) => m.tempId === mediaItem.tempId);
    }
    return -1;
  }

  private getActiveVideoUploadCount(): number {
    let active = 0;
    this.uploadProgressMap.forEach((progress: UploadProgress) => {
      if (progress && progress.status === "uploading") {
        active++;
      }
    });
    return active;
  }

  private getAutoThumbnailConcurrencyLimit(): number {
    // Keep thumbnail uploads light while video multipart upload is running.
    return this.getActiveVideoUploadCount() > 0 ? 1 : 2;
  }

  private enqueueAutoThumbnailJob(job: () => Promise<void>): void {
    this.pendingAutoThumbnailJobs.push(job);
    this.drainAutoThumbnailQueue();
  }

  private drainAutoThumbnailQueue(): void {
    if (this.isDestroying) {
      this.pendingAutoThumbnailJobs = [];
      return;
    }
    const limit = this.getAutoThumbnailConcurrencyLimit();
    while (this.runningAutoThumbnailJobs < limit && this.pendingAutoThumbnailJobs.length > 0) {
      const nextJob = this.pendingAutoThumbnailJobs.shift();
      if (!nextJob) {
        break;
      }
      this.runningAutoThumbnailJobs++;
      nextJob()
        .catch(err => {
          console.error("Auto thumbnail queue job failed:", err);
        })
        .then(() => {
          this.runningAutoThumbnailJobs = Math.max(0, this.runningAutoThumbnailJobs - 1);
          this.drainAutoThumbnailQueue();
        });
    }
  }

  /**
   * Give thumbnail generation/upload a short head-start before multipart video upload.
   * This improves thumbnail success rate when network/CPU are saturated by large video uploads.
   */
  private async prioritizeThumbnailBeforeVideoUpload(file: File, mediaItem: any): Promise<void> {
    const queuedJob = new Promise<void>(resolve => {
      this.enqueueAutoThumbnailJob(async () => {
        try {
          if (this.isDestroying) {
            return;
          }
          const idx = this.findMediaIndexByClientKey(mediaItem);
          if (idx === -1) {
            return;
          }
          await this.generateThumbnail(file, mediaItem);
        } finally {
          resolve();
        }
      });
    });

    // Wait briefly for thumbnail to complete; then continue video upload regardless.
    const headStartMs = 8000;
    await Promise.race([
      queuedJob,
      new Promise<void>(resolve => {
        setTimeout(() => resolve(), headStartMs);
      }),
    ]);
  }

  private resolveMediaIndexForAsyncThumbnailSwap(seed: any, fallbackIndex: number): number {
    if (!this.subsection.media.length) {
      return -1;
    }
    if (seed.mediaClientKey) {
      const byKey = this.subsection.media.findIndex((m: any) => m.mediaClientKey === seed.mediaClientKey);
      if (byKey !== -1) {
        return byKey;
      }
    }
    if (seed.id != null) {
      const byId = this.subsection.media.findIndex((m: any) => m.id === seed.id);
      if (byId !== -1) {
        return byId;
      }
    }
    if (seed.tempId) {
      const byTemp = this.subsection.media.findIndex((m: any) => m.tempId === seed.tempId);
      if (byTemp !== -1) {
        return byTemp;
      }
    }
    if (seed.url && seed.fileName) {
      const byUrlAndName = this.subsection.media.findIndex(
        (m: any) => m.url === seed.url && m.fileName === seed.fileName
      );
      if (byUrlAndName !== -1) {
        return byUrlAndName;
      }
    }
    if (fallbackIndex >= 0 && fallbackIndex < this.subsection.media.length) {
      return fallbackIndex;
    }
    return -1;
  }

  /**
   * Keep local preview visible until S3 image has been preloaded successfully.
   */
  private preloadAndSwapToS3Thumbnail(mediaIndex: number, s3Url: string): void {
    if (!s3Url || this.isDestroying || !this.subsection.media.length) {
      return;
    }
    const current = this.subsection.media[mediaIndex];
    if (!current) {
      return;
    }
    const seed = {
      mediaClientKey: current.mediaClientKey,
      id: current.id,
      tempId: current.tempId,
      fileName: current.fileName,
      url: current.url,
    };
    const indexNow = this.resolveMediaIndexForAsyncThumbnailSwap(seed, mediaIndex);
    if (indexNow === -1) {
      return;
    }

    const localPreview =
      this.subsection.media[indexNow].thumbnailLocalPreview || this.subsection.media[indexNow].thumbnail;
    this.subsection.media[indexNow] = {
      ...this.subsection.media[indexNow],
      thumbnailS3Pending: s3Url,
      thumbnailLocalPreview: localPreview || null,
    };
    this.subsection.media = [...this.subsection.media];
    this.modelChange.emit();

    const preloadImg = new Image();
    preloadImg.onload = () => {
      const latestIndex = this.resolveMediaIndexForAsyncThumbnailSwap(seed, mediaIndex);
      if (latestIndex === -1 || !this.subsection.media[latestIndex]) {
        return;
      }
      this.subsection.media[latestIndex] = {
        ...this.subsection.media[latestIndex],
        thumbnail: s3Url,
        thumbnailLoading: true,
        thumbnailProcessing: true,
        needsThumbnailRetry: false,
      };
      delete this.subsection.media[latestIndex].thumbnailS3Pending;
      delete this.subsection.media[latestIndex].thumbnailLocalPreview;
      this.subsection.media = [...this.subsection.media];
      this.modelChange.emit();
      persistExistingVideoThumbnail(
        this.travelKitService,
        this.subsection.media[latestIndex],
        this.subsection && this.subsection.id
      );
    };
    preloadImg.onerror = () => {
      const latestIndex = this.resolveMediaIndexForAsyncThumbnailSwap(seed, mediaIndex);
      if (latestIndex === -1 || !this.subsection.media[latestIndex]) {
        return;
      }
      // Keep local preview and retry later.
      this.subsection.media[latestIndex] = {
        ...this.subsection.media[latestIndex],
        needsThumbnailRetry: true,
      };
      delete this.subsection.media[latestIndex].thumbnailS3Pending;
      this.subsection.media = [...this.subsection.media];
      this.modelChange.emit();
    };
    preloadImg.src = s3Url;
  }

  /**
   * Tab background / SPA navigation throttles video decode; retry URL-based thumbnail when user returns.
   */
  private retryMissingVideoThumbnails(): void {
    if (this.isDestroying || !this.subsection.media.length) {
      return;
    }
    if (document.visibilityState !== "visible") {
      return;
    }
    this.subsection.media.forEach((m: any) => {
      if (m.type !== "video" || !m.url || this.isBlobUrl(m.url)) {
        return;
      }
      const thumb = m.thumbnail;
      if (thumb != null && String(thumb).trim() !== "") {
        return;
      }
      this.ensureMediaClientKeyForRow(m);
      void this.ensureThumbnailFromUploadedUrl(m.url, m.fileName, m.mediaClientKey);
    });
  }

  private thumbnailFromUrlDedupeKey(videoUrl: string, mediaClientKey?: string, fileName?: string): string {
    return mediaClientKey ? `k:${mediaClientKey}` : `u:${videoUrl}::${fileName || ""}`;
  }

  /**
   * Rows loaded from API often lack `mediaClientKey`; assign a stable one so thumbnail jobs never cross wires.
   */
  private ensureMediaClientKeyForRow(m: any): void {
    if (!m || m.mediaClientKey) {
      return;
    }
    if (m.id != null && String(m.id).trim() !== "" && Number(m.id) > 0) {
      m.mediaClientKey = `mc_srv_${m.id}`;
      return;
    }
    const u = m.url && typeof m.url === "string" ? m.url : "";
    const fn = m.fileName && typeof m.fileName === "string" ? m.fileName : "";
    m.mediaClientKey = `mc_legacy_${this.shortHash(u + "::" + fn)}`;
  }

  private shortHash(s: string): string {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h).toString(36);
  }

  private isDataUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return String(url).startsWith("data:");
  }

  private dataUrlToBlob(dataUrl: string): Blob | null {
    try {
      const arr = dataUrl.split(",");
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch || !arr[1]) return null;
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } catch {
      return null;
    }
  }

  private migrateBase64ThumbnailsToS3(): void {
    if (!this.subsection || !this.subsection.media) return;
    this.subsection.media.forEach((m: any) => {
      if (m.type !== "video") return;
      if (!m.thumbnail || !this.isDataUrl(m.thumbnail)) return;
      this.ensureMediaClientKeyForRow(m);
      void this.uploadBase64ThumbnailToS3(m.mediaClientKey, m.thumbnail);
    });
  }

  private uploadBase64ThumbnailToS3(mediaClientKey: string, dataUrl: string): Promise<void> {
    const blob = this.dataUrlToBlob(dataUrl);
    if (!blob) return Promise.resolve();
    const timestamp = Date.now();
    const filename = `thumbnail_migrate_${timestamp}.jpg`;
    const file = new File([blob], filename, { type: "image/jpeg" });
    const uploadKey = `thumb_migrate_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

    const idx = this.subsection.media.findIndex((m: any) => m.mediaClientKey === mediaClientKey);
    if (idx === -1) return Promise.resolve();

    this.subsection.media[idx] = {
      ...this.subsection.media[idx],
      thumbnail: null,
      thumbnailLocalPreview: dataUrl,
      thumbnailProcessing: true,
      thumbnailUploadKey: uploadKey,
    };
    this.setThumbnailUploadState(uploadKey, true);
    this.subsection.media = [...this.subsection.media];
    this.modelChange.emit();

    return new Promise<void>(resolve => {
      this.uploadService.uploadFileToS3("digital/thumb", file, 5).subscribe({
        next: (response: any) => {
          if (response && response.type === "complete") {
            const latestIdx = this.subsection.media.findIndex((m: any) => m.mediaClientKey === mediaClientKey);
            if (latestIdx !== -1) {
              this.subsection.media[latestIdx] = {
                ...this.subsection.media[latestIdx],
                thumbnail: response.url,
                thumbnailLocalPreview: null,
                thumbnailLoading: true,
                thumbnailProcessing: false,
                thumbnailUploadKey: null,
                needsThumbnailRetry: false,
              };
              this.subsection.media = [...this.subsection.media];
              this.modelChange.emit();
            }
            this.setThumbnailUploadState(uploadKey, false);
            this.cleanupThumbnailUpload(uploadKey);
            resolve();
          }
        },
        error: err => {
          console.error("Failed to migrate base64 thumbnail to S3:", err);
          const latestIdx = this.subsection.media.findIndex((m: any) => m.mediaClientKey === mediaClientKey);
          if (latestIdx !== -1) {
            this.subsection.media[latestIdx] = {
              ...this.subsection.media[latestIdx],
              thumbnail: null,
              thumbnailLocalPreview: null,
              thumbnailProcessing: false,
              thumbnailUploadKey: null,
              needsThumbnailRetry: true,
            };
            this.subsection.media = [...this.subsection.media];
            this.modelChange.emit();
          }
          this.setThumbnailUploadState(uploadKey, false);
          this.cleanupThumbnailUpload(uploadKey);
          resolve();
        },
      });
    });
  }

  /** Row that still needs a thumbnail (empty), for starting / error handling */
  private findMediaRowNeedingThumbnailFromUrl(videoUrl: string, fileName?: string, mediaClientKey?: string): number {
    if (!this.subsection.media) {
      return -1;
    }
    const needsThumb = (m: any): boolean => {
      if (!m) {
        return false;
      }
      const t = m.thumbnail;
      if (t === null || t === undefined || String(t).trim() === "") {
        return true;
      }
      if (typeof t === "string" && t.trim().startsWith("data:")) {
        return true;
      }
      return false;
    };

    if (mediaClientKey) {
      const byKeyUrl = this.subsection.media.findIndex(
        (m: any) => m.mediaClientKey === mediaClientKey && m.url === videoUrl && needsThumb(m)
      );
      if (byKeyUrl !== -1) {
        return byKeyUrl;
      }
      const byKey = this.subsection.media.findIndex((m: any) => m.mediaClientKey === mediaClientKey && needsThumb(m));
      if (byKey !== -1) {
        return byKey;
      }
    }

    return this.subsection.media.findIndex(
      (m: any) => m && m.url === videoUrl && needsThumb(m) && (!fileName || m.fileName === fileName)
    );
  }

  /** Row to assign thumbnail to after generation (same video), regardless of current thumb state */
  private findMediaRowForThumbnailAssign(videoUrl: string, fileName?: string, mediaClientKey?: string): number {
    if (!this.subsection.media) {
      return -1;
    }
    if (mediaClientKey) {
      const byKeyUrl = this.subsection.media.findIndex(
        (m: any) => m.mediaClientKey === mediaClientKey && m.url === videoUrl
      );
      if (byKeyUrl !== -1) {
        return byKeyUrl;
      }
      const byKey = this.subsection.media.findIndex((m: any) => m.mediaClientKey === mediaClientKey);
      if (byKey !== -1) {
        return byKey;
      }
    }
    return this.subsection.media.findIndex(
      (m: any) => m && m.url === videoUrl && (!fileName || m.fileName === fileName)
    );
  }

  /**
   * Save upload state to localStorage for resume capability
   */
  private saveUploadState(fileId: string, state: UploadState): void {
    try {
      const stateToSave = {
        ...state,
        file: null, // Don't save File object
      };
      localStorage.setItem(`${this.STORAGE_PREFIX}${fileId}`, JSON.stringify(stateToSave));
    } catch (error) {
      console.warn("Failed to save upload state:", error);
    }
  }

  /**
   * Load upload state from localStorage
   */
  private loadUploadState(fileId: string): UploadState | null {
    try {
      const saved = localStorage.getItem(`${this.STORAGE_PREFIX}${fileId}`);
      if (!saved) return null;
      return JSON.parse(saved);
    } catch (error) {
      console.warn("Failed to load upload state:", error);
      return null;
    }
  }

  /**
   * Remove upload state from localStorage
   */
  private removeUploadState(fileId: string): void {
    try {
      localStorage.removeItem(`${this.STORAGE_PREFIX}${fileId}`);
    } catch (error) {
      console.warn("Failed to remove upload state:", error);
    }
  }

  /**
   * Update progress
   */
  private updateProgress(
    fileId: string,
    percent: number,
    status: UploadProgress["status"],
    uploadedParts?: number,
    totalParts?: number
  ): void {
    const progress: UploadProgress = {
      fileId,
      percent,
      status,
      uploadedParts,
      totalParts,
    };
    this.uploadProgressMap.set(fileId, progress);
    this.uploadProgress.emit(progress);
  }

  /**
   * Main upload function
   */
  async uploadFiles(files: FileList | File[]): Promise<void> {
    const fileArray = Array.from(files);
    fileArray.sort((a, b) => a.name.localeCompare(b.name));

    for (const file of fileArray) {
      const fileId = this.generateFileId(file);
      await this.uploadSingleFile(file, fileId);
    }
  }

  /**
   * Upload a single file with resume capability
   */
  async uploadSingleFile(
    file: File,
    fileId: string,
    isResume: boolean = false,
    autoRetryAttempt: number = 1
  ): Promise<void> {
    try {
      console.log("uploadSingleFile", file, fileId, isResume);

      // Check for existing upload state (resume)
      let state = this.loadUploadState(fileId);
      const memoryState = this.uploadStates.get(fileId);
      const isResuming = !!state && isResume;

      // Check resume count limit (max 3 attempts)
      if (isResuming && state) {
        const currentResumeCount = state.resumeCount || 0;
        if (currentResumeCount >= 3) {
          const errorMsg = "Maximum resume attempts (3) reached. Please start a new upload.";
          this.toastr.error(errorMsg, "Resume Limit Reached");

          // Update media item to show error
          if (state.mediaItem) {
            const mediaIndex = this.subsection.media.findIndex((m: any) => m.tempId === state.mediaItem.tempId);
            if (mediaIndex > -1) {
              this.subsection.media[mediaIndex] = {
                ...this.subsection.media[mediaIndex],
                uploadKey: null,
                uploadError: errorMsg,
                canResume: false,
              };
              this.modelChange.emit();
            }
          }
          throw new Error(errorMsg);
        }

        // Increment resume count
        state.resumeCount = currentResumeCount + 1;
        console.log(`🔄 Resuming upload (attempt ${state.resumeCount}/3): ${file.name}`);
      }

      if (!state) {
        this.updateProgress(fileId, 0, "pending");

        if (this.isDestroying || this.uploadCancelled.get(fileId)) {
          throw new Error("Upload cancelled");
        }

        state = {
          fileId,
          fileName: file.name,
          file,
          uploadId: "",
          key: "",
          partsCount: 0,
          uploadedParts: [],
          presignedUrls: [],
          currentPartIndex: 0,
          sectionIndex: this.sectionIndex,
          subIndex: this.subIndex,
          mediaItem: memoryState && memoryState.mediaItem ? memoryState.mediaItem : null,
          folder: this.folder,
          fileType: file.type || "video/mp4",
          resumeCount: 0,
        };

        this.uploadStates.set(fileId, state);
      } else {
        state.file = file;
        if (!state.mediaItem && memoryState && memoryState.mediaItem) {
          state.mediaItem = memoryState.mediaItem;
        }
        if (!state.mediaItem) {
          const mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
          if (mediaIndex !== -1) {
            state.mediaItem = this.subsection.media[mediaIndex];
          }
        }
        this.uploadStates.set(fileId, state);
        if (isResuming) {
          this.toastr.info(`Resuming upload: ${file.name}`);
        }
      }

      const resume: MultipartResumePayload | undefined =
        state.uploadId && state.key && state.partsCount
          ? {
              key: state.key,
              uploadId: state.uploadId,
              partsCount: state.partsCount,
              presignedUrls: state.presignedUrls || [],
              uploadedParts: state.uploadedParts || [],
              currentPartIndex: state.currentPartIndex != null ? state.currentPartIndex : 0,
            }
          : undefined;

      let finalUrl: string;
      try {
        finalUrl = await new Promise<string>((resolve, reject) => {
          let completeUrl: string | undefined;
          const sub = this.multipartS3Upload
            .uploadVideoMultipart(this.folder, file, {
              maxSizeMB: this.maxSizeMB,
              chunkSizeMB: this.chunkSizeMB,
              fileId,
              resume,
              shouldCancel: () => this.isDestroying || !!this.uploadCancelled.get(fileId),
              onCheckpoint: cp => {
                state.uploadedParts = cp.uploadedParts;
                state.currentPartIndex = cp.currentPartIndex;
                state.presignedUrls = cp.presignedUrls;
                state.key = cp.key;
                state.uploadId = cp.uploadId;
                state.partsCount = cp.partsCount;
                state.file = file;
                this.uploadStates.set(fileId, state);
                this.saveUploadState(fileId, state);
              },
            })
            .subscribe({
              next: ev => {
                if (ev.type === "progress") {
                  this.updateProgress(fileId, ev.percent, "uploading", ev.uploadedParts, ev.totalParts);
                } else if (ev.type === "complete") {
                  completeUrl = ev.url;
                }
              },
              error: err => {
                this.uploadStreamSubs.delete(fileId);
                reject(err instanceof Error ? err : new Error(String(err)));
              },
              complete: () => {
                this.uploadStreamSubs.delete(fileId);
                if (completeUrl) {
                  resolve(completeUrl);
                } else {
                  reject(new Error("Upload finished without URL"));
                }
              },
            });
          this.uploadStreamSubs.set(fileId, sub);
        });
      } catch (error) {
        const errorMsg = this.getReadableUploadError(error);

        if (this.isDestroying || errorMsg.includes("cancelled") || errorMsg.includes("aborted")) {
          this.uploadCancelled.set(fileId, true);
          this.removeUploadState(fileId);
          this.uploadStates.delete(fileId);
          this.uploadProgressMap.delete(fileId);
          return;
        }

        if (autoRetryAttempt < this.AUTO_RETRY_MAX_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, this.AUTO_RETRY_DELAY_MS));
          await this.uploadSingleFile(file, fileId, isResume, autoRetryAttempt + 1);
          return;
        }

        const uploadedLen = state.uploadedParts ? state.uploadedParts.length : 0;
        const allPartsUploaded =
          state.partsCount > 0 && uploadedLen === state.partsCount && !errorMsg.toLowerCase().includes("cancelled");

        if (allPartsUploaded) {
          console.error(`❌ Complete multipart upload failed for ${file.name}:`, errorMsg);

          if (state.resumeCount === undefined) {
            state.resumeCount = 0;
          }

          this.uploadProgressMap.delete(fileId);

          const currentResumeCount = state.resumeCount || 0;

          let mediaIndex = -1;
          mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
          if (mediaIndex === -1 && state.mediaItem && state.mediaItem.tempId) {
            mediaIndex = this.subsection.media.findIndex((m: any) => m.tempId === state.mediaItem.tempId);
          }
          if (mediaIndex === -1) {
            mediaIndex = this.subsection.media.findIndex((m: any) => m.fileName === file.name && m.uploadKey);
          }

          if (mediaIndex > -1) {
            this.subsection.media[mediaIndex] = {
              ...this.subsection.media[mediaIndex],
              uploadKey: null,
              uploadError: errorMsg,
              isPending: false,
              canRetryComplete: false,
              canResume: currentResumeCount < 3,
              resumeCount: currentResumeCount,
            };
          }

          this.saveUploadState(fileId, state);

          this.uploadError.emit({
            file,
            error: `Failed to complete upload: ${errorMsg}`,
            fileId,
          });

          const canResume = state.resumeCount < 3;
          const resumeInfo = canResume
            ? ` You can resume upload (${3 - state.resumeCount} attempts remaining).`
            : " Maximum resume attempts (3) reached.";
          this.toastr.error(`Failed to complete upload: ${errorMsg}.${resumeInfo}`, "Upload Error", {
            timeOut: 7000,
          });

          this.modelChange.emit();
          return;
        }

        console.error(`❌ Upload failed for ${file.name}:`, errorMsg);

        this.updateProgress(fileId, 0, "error");

        if (state.mediaItem) {
          const mediaIndex = this.subsection.media.findIndex((m: any) => m.tempId === state.mediaItem.tempId);
          if (mediaIndex > -1) {
            this.subsection.media[mediaIndex] = {
              ...this.subsection.media[mediaIndex],
              uploadKey: null,
              uploadError: errorMsg,
              isPending: false,
            };
          }
        }

        this.uploadCancelled.set(fileId, true);
        this.removeUploadState(fileId);
        this.uploadStates.delete(fileId);
        this.uploadProgressMap.delete(fileId);

        this.uploadError.emit({
          file,
          error: errorMsg,
          fileId,
        });

        this.modelChange.emit();
        return;
      }

      if (this.isDestroying || this.uploadCancelled.get(fileId)) {
        return;
      }

      // Cleanup
      this.removeUploadState(fileId);
      this.uploadStates.delete(fileId);
      this.updateProgress(fileId, 100, "completed");

      // Update media item with final URL
      // Suppress logs if component is being destroyed
      if (!this.isDestroying) {
        console.log(`🔍 Attempting to save URL to media item. finalUrl:`, finalUrl);
        console.log(`🔍 state.mediaItem:`, state.mediaItem);
        console.log(`🔍 state.fileId:`, fileId);
        console.log(`🔍 subsection.media length:`, this.subsection.media.length);
        console.log(
          `🔍 subsection.media:`,
          this.subsection.media.map((m: any) => ({
            tempId: m.tempId,
            fileName: m.fileName,
            url: m.url,
            uploadKey: m.uploadKey,
          }))
        );
      }

      // Try multiple ways to find the media item
      let mediaIndex = -1;

      // Method 1: Find by tempId from state.mediaItem
      if (state.mediaItem && state.mediaItem.tempId) {
        const tempIdToFind = state.mediaItem.tempId;
        if (!this.isDestroying) {
          console.log(`🔍 Method 1: Looking for media item with tempId:`, tempIdToFind);
        }
        mediaIndex = this.subsection.media.findIndex((m: any) => m.tempId === tempIdToFind);
        if (!this.isDestroying) {
          console.log(`🔍 Method 1 result - Found media index:`, mediaIndex);
        }
      }

      // Method 2: Find by uploadKey (fileId)
      if (mediaIndex === -1 && fileId) {
        if (!this.isDestroying) {
          console.log(`🔍 Method 2: Looking for media item with uploadKey (fileId):`, fileId);
        }
        mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
        if (!this.isDestroying) {
          console.log(`🔍 Method 2 result - Found media index:`, mediaIndex);
        }
      }

      // Method 3: Find by fileName (last resort)
      if (mediaIndex === -1 && state.fileName) {
        if (!this.isDestroying) {
          console.log(`🔍 Method 3: Looking for media item with fileName:`, state.fileName);
        }
        mediaIndex = this.subsection.media.findIndex((m: any) => m.fileName === state.fileName && m.uploadKey);
        if (!this.isDestroying) {
          console.log(`🔍 Method 3 result - Found media index:`, mediaIndex);
        }
      }

      if (mediaIndex !== -1) {
        if (!this.isDestroying) {
          console.log(`✅ Found media item at index ${mediaIndex}, saving URL:`, finalUrl);
          console.log(`📝 Before update:`, {
            url: this.subsection.media[mediaIndex].url,
            fileName: this.subsection.media[mediaIndex].fileName,
            uploadKey: this.subsection.media[mediaIndex].uploadKey,
            tempId: this.subsection.media[mediaIndex].tempId,
          });
        }

        // Create new object to ensure change detection
        const updatedMedia = {
          ...this.subsection.media[mediaIndex],
          url: finalUrl, // Ensure URL is saved
          uploadKey: null, // Clear uploadKey after successful upload
          isPending: false,
        };

        // Only delete tempId if it exists
        if (updatedMedia.tempId) {
          delete updatedMedia.tempId;
        }

        // Update the media item
        this.subsection.media[mediaIndex] = updatedMedia;

        if (!this.isDestroying) {
          console.log(`✅ Media item after update:`, {
            url: this.subsection.media[mediaIndex].url,
            fileName: this.subsection.media[mediaIndex].fileName,
            uploadKey: this.subsection.media[mediaIndex].uploadKey,
            tempId: this.subsection.media[mediaIndex].tempId,
          });
        }

        // Force change detection by creating new array reference
        this.subsection.media = [...this.subsection.media];

        // Retry thumbnail generation in background so next video can start upload immediately.
        void this.retryGenerateThumbnailAfterUpload(file, mediaIndex, finalUrl);
        // Safety net: if thumbnail is still empty after upload, generate from uploaded video URL.
        void this.ensureThumbnailFromUploadedUrl(finalUrl, state.fileName, updatedMedia.mediaClientKey);

        // Double check after array recreation
        if (!this.isDestroying) {
          console.log(`✅ Final check - URL in media item:`, this.subsection.media[mediaIndex].url);
        }
      } else {
        // Suppress error messages if component is being destroyed
        if (this.isDestroying) {
          // Silently return - component is being destroyed, media item was already removed
          return;
        }

        console.error(`❌ Could not find media item using any method`);
        console.error(
          `Available media items:`,
          this.subsection.media.map((m: any) => ({
            tempId: m.tempId,
            fileName: m.fileName,
            uploadKey: m.uploadKey,
            url: m.url,
          }))
        );
        console.error(`Search criteria:`, {
          tempId: state.mediaItem ? state.mediaItem.tempId : null,
          fileId: fileId,
          fileName: state.fileName,
        });

        // If media item not found and component is not destroying, it's a real error
        // But don't emit completion or show success toast
        return;
      }

      // Suppress completion events if component is being destroyed
      if (this.isDestroying) {
        return;
      }

      // Emit completion
      this.uploadComplete.emit({
        file,
        url: finalUrl,
        fileId,
        mediaItem: state.mediaItem,
      });

      this.toastr.success(`Video ${file.name} uploaded successfully`);
      this.modelChange.emit();
    } catch (error) {
      // Mark upload as cancelled/stopped
      this.uploadCancelled.set(fileId, true);

      const errorMsg = this.getReadableUploadError(error);
      // Suppress error messages if component is being destroyed or upload was cancelled
      if (this.isDestroying || errorMsg.includes("cancelled") || errorMsg.includes("aborted")) {
        // Silently cleanup without showing error messages
        const mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
        if (mediaIndex > -1) {
          this.subsection.media.splice(mediaIndex, 1);
        }
        this.removeUploadState(fileId);
        this.uploadStates.delete(fileId);
        this.uploadProgressMap.delete(fileId);
        return;
      }

      console.error(`❌ Upload error for ${file.name}:`, errorMsg);

      // Update progress to error state
      this.updateProgress(fileId, 0, "error");

      // Find and update media item - clear uploadKey to hide progress bar
      const mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
      if (mediaIndex > -1) {
        // Clear uploadKey to hide progress bar and mark as error
        this.subsection.media[mediaIndex] = {
          ...this.subsection.media[mediaIndex],
          uploadKey: null, // Clear uploadKey to hide progress bar
          uploadError: errorMsg, // Store error message
          isPending: false, // Mark as not pending
        };
      }

      // Cleanup
      this.removeUploadState(fileId);
      this.uploadStates.delete(fileId);
      this.uploadProgressMap.delete(fileId);

      // Emit error event
      this.uploadError.emit({
        file,
        error: errorMsg,
        fileId,
      });

      // Show error notification
      this.toastr.error(`Upload failed: ${file.name} - ${errorMsg}`, "Upload Error", {
        timeOut: 5000,
      });

      // Emit model change to update UI
      this.modelChange.emit();
    }
  }

  /**
   * Resume a paused/failed upload
   */
  async resumeUpload(fileId: string, file?: File): Promise<void> {
    const state = this.loadUploadState(fileId);
    if (!state) {
      this.toastr.error("Cannot resume: upload state not found");
      return;
    }

    // If file is provided, use it; otherwise try to find it from media item
    let fileToUpload = file;
    if (!fileToUpload && state.mediaItem) {
      const mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
      if (mediaIndex !== -1) {
        // File reference might be lost, need to get it from user
        this.toastr.warning("Please select the file again to resume upload");
        return;
      }
    }

    if (!fileToUpload) {
      this.toastr.error("File not found for resume");
      return;
    }

    await this.uploadSingleFile(fileToUpload, fileId);
  }

  /**
   * Cancel an upload
   */
  cancelUpload(fileId: string): void {
    // Mark upload as cancelled
    this.uploadCancelled.set(fileId, true);

    const streamSub = this.uploadStreamSubs.get(fileId);
    if (streamSub) {
      streamSub.unsubscribe();
      this.uploadStreamSubs.delete(fileId);
    }

    const state = this.uploadStates.get(fileId);
    if (state) {
      this.updateProgress(fileId, 0, "error");
      this.saveUploadState(fileId, state);
    }

    console.log(`🚫 Upload cancelled for fileId: ${fileId}`);
  }

  /**
   * Get progress for a file
   */
  getProgress(fileId: string): UploadProgress | null {
    return this.uploadProgressMap.get(fileId) || null;
  }

  /**
   * Check if upload is in progress
   */
  isUploading(fileId: string): boolean {
    const progress = this.uploadProgressMap.get(fileId);
    return progress ? progress.status === "uploading" : false;
  }

  /**
   * Generate unique file ID
   */
  private generateFileId(file: File): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `upload_${timestamp}_${random}_${file.name}`;
  }

  /**
   * Clean up all upload states (call on component destroy)
   */
  cleanup(): void {
    this.uploadStates.clear();
    this.uploadProgressMap.clear();
  }

  /**
   * Handle file selection
   */
  async onFileSelected(event: any): Promise<void> {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Check current total including pending uploads
    const currentTotalCount = this.getTotalVideoCountInSection();
    if (currentTotalCount + files.length > this.maxVideosPerSection) {
      this.toastr.error(
        `Maximum ${this.maxVideosPerSection} videos are allowed per section. You cannot add more videos.`
      );
      // Reset input
      if (event.target) {
        event.target.value = "";
      }
      return;
    }

    // Validate files
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const acceptedVideoTypes = [
        "video/mp4",
        "video/avi",
        "video/x-msvideo",
        "video/quicktime",
        "video/x-ms-wmv",
        "video/x-flv",
        "video/x-matroska",
        "video/mkv",
        "video/webm",
      ];

      if (!acceptedVideoTypes.includes(file.type.toLowerCase())) {
        this.toastr.error(
          `File ${file.name} format not supported. Accepted formats: MP4, AVI, MOV, WMV, FLV, MKV, WEBM`
        );
        continue;
      }

      if (file.size / 1024 / 1024 >= this.maxSizeMB) {
        const maxSizeGB = (this.maxSizeMB / 1024).toFixed(1);
        this.toastr.error(
          `File ${file.name} size is ${this.formatFileSize(file.size)}. Maximum size is ${maxSizeGB}GB. Please compress your video or use a smaller file.`
        );
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    validFiles.sort((a, b) => a.name.localeCompare(b.name));

    // Initialize media array if needed
    if (!this.subsection.media) {
      this.subsection.media = [];
    }

    // Process all files sequentially (one after another)
    // This ensures each video uploads completely before starting the next one
    this.processFilesSequentially(validFiles).catch(error => {
      console.error("Error processing files:", error);
    });

    // Reset input
    if (event.target) {
      event.target.value = "";
    }
  }

  /**
   * Process multiple files sequentially (one after another)
   */
  private async processFilesSequentially(files: File[]): Promise<void> {
    console.log(`📋 Processing ${files.length} files sequentially...`);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`📤 Starting upload ${i + 1}/${files.length}: ${file.name}`);

      try {
        await this.processFile(file);
        console.log(`✅ Completed upload ${i + 1}/${files.length}: ${file.name}`);
      } catch (error) {
        console.error(`❌ Failed to upload ${i + 1}/${files.length}: ${file.name}`, error);
        // Continue with next file even if current one fails
        this.toastr.error(`Failed to upload ${file.name}: ${this.getReadableUploadError(error)}`, "Upload Error");
      }

      // Small delay between uploads to avoid overwhelming the server
      if (i < files.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Sort toàn bộ media theo fileName
    if (this.subsection.media && this.subsection.media.length > 1) {
      this.subsection.media.sort((a, b) => (a.fileName || "").localeCompare(b.fileName || ""));
    }

    console.log(`✅ Finished processing all ${files.length} files`);
  }

  /**
   * Process a single file
   */
  private async processFile(file: File): Promise<void> {
    const fileId = this.generateFileId(file);
    const timestamp = Date.now();
    const tempId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

    // Create media item
    const mediaItem = {
      fileName: file.name,
      url: null,
      size: file.size,
      type: "video",
      uploadKey: fileId,
      tempId: tempId,
      /** Stable id for thumbnail callbacks after `tempId` is cleared post-upload */
      mediaClientKey: `mc_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
      thumbnail: null,
      isPending: true,
    };

    // Initialize media array if needed
    if (!this.subsection.media) {
      this.subsection.media = [];
    }

    this.subsection.media.push(mediaItem);

    try {
      // Prioritize thumbnail before video upload to avoid black/no-preview cards.
      await this.prioritizeThumbnailBeforeVideoUpload(file, mediaItem);

      // Get or create upload state
      let state = this.uploadStates.get(fileId);
      if (!state) {
        state = {
          fileId,
          fileName: file.name,
          file,
          uploadId: "",
          key: "",
          partsCount: 0,
          uploadedParts: [],
          presignedUrls: [],
          currentPartIndex: 0,
          sectionIndex: this.sectionIndex,
          subIndex: this.subIndex,
          mediaItem: mediaItem,
          folder: this.folder,
          fileType: file.type || "video/mp4",
        };
        this.uploadStates.set(fileId, state);
      } else {
        state.mediaItem = mediaItem;
        state.file = file;
      }

      // Start upload
      await this.uploadSingleFile(file, fileId);
    } catch (error) {
      console.error(`Failed to process ${file.name}:`, error);
      // Remove media item on error
      const index = this.findMediaIndexByClientKey(mediaItem);
      if (index > -1) {
        this.subsection.media.splice(index, 1);
      }
      this.toastr.error(`Failed to process ${file.name}: ${error.message || error}`);
    }
  }

  /**
   * Generate thumbnail for video
   */
  private async generateThumbnail(file: File, mediaItem: any): Promise<void> {
    return new Promise<void>(resolve => {
      const thumbnailUploadKey = `thumb_auto_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      mediaItem.thumbnailProcessing = true;
      mediaItem.thumbnailUploadKey = thumbnailUploadKey;
      this.setThumbnailUploadState(thumbnailUploadKey, true);
      // Show visible progress while generating thumbnail frame before upload starts.
      this.setThumbnailUploadProgress(thumbnailUploadKey, 10);
      this.startThumbnailProgressSimulation(thumbnailUploadKey);
      this.modelChange.emit();

      this.videoThumbnailService.generateThumbnailFromVideo(file, "digital/thumb", 5).subscribe(
        (response: any) => {
          const mediaIndex = this.findMediaIndexByClientKey(mediaItem);

          // Keep the current media item in sync so template can always read upload key/progress source.
          if (mediaIndex !== -1) {
            this.subsection.media[mediaIndex] = {
              ...this.subsection.media[mediaIndex],
              thumbnailProcessing: true,
              thumbnailUploadKey: thumbnailUploadKey,
            };
          }

          if (response && response.type === "progress") {
            const rawPercent = typeof response.percent === "number" ? response.percent : 0;
            // Prefer real progress when available, but avoid jumping to near-complete too early.
            const current = this.getThumbnailProgressByKey(thumbnailUploadKey);
            const displayPercent = Math.min(98, Math.max(current, rawPercent, 10));
            this.setThumbnailUploadProgress(thumbnailUploadKey, displayPercent);
            if (mediaIndex !== -1) {
              this.subsection.media = [...this.subsection.media];
            }
            this.modelChange.emit();
            return;
          }

          if (response && response.type === "preview" && response.url) {
            // Show poster immediately from local data URL; continue uploading final S3 thumbnail in background.
            const current = this.getThumbnailProgressByKey(thumbnailUploadKey);
            this.setThumbnailUploadProgress(thumbnailUploadKey, Math.max(current, 30));
            if (mediaIndex !== -1) {
              this.subsection.media[mediaIndex] = {
                ...this.subsection.media[mediaIndex],
                thumbnail: response.url,
                thumbnailLocalPreview: response.url,
                thumbnailLoading: true,
                thumbnailProcessing: true,
              };
              this.subsection.media = [...this.subsection.media];
            }
            this.modelChange.emit();
            return;
          }

          if (response && response.type === "complete") {
            this.stopThumbnailProgressSimulation(thumbnailUploadKey);
            if (mediaIndex !== -1) {
              const s3Url = response.s3Url || null;
              const hasLocalPreview = !!this.subsection.media[mediaIndex].thumbnailLocalPreview;
              if (s3Url && hasLocalPreview) {
                this.preloadAndSwapToS3Thumbnail(mediaIndex, s3Url);
              } else {
                const base = this.subsection.media[mediaIndex];
                const nextThumb =
                  response.url && String(response.url).trim() !== ""
                    ? response.url
                    : base.thumbnail || base.thumbnailLocalPreview || null;
                const displayable = this.hasDisplayableThumbnail({ ...base, thumbnail: nextThumb });
                this.subsection.media[mediaIndex] = {
                  ...base,
                  thumbnail: nextThumb,
                  needsThumbnailRetry: !displayable,
                  thumbnailLoading: displayable,
                  thumbnailProcessing: displayable,
                };
                if (displayable) {
                  persistExistingVideoThumbnail(
                    this.travelKitService,
                    this.subsection.media[mediaIndex],
                    this.subsection && this.subsection.id
                  );
                }
              }
              if (this.subsection.media[mediaIndex].thumbnailUploadKey === thumbnailUploadKey) {
                delete this.subsection.media[mediaIndex].thumbnailUploadKey;
              }
              this.subsection.media = [...this.subsection.media];
              this.modelChange.emit();
            }
            this.setThumbnailUploadProgress(thumbnailUploadKey, 100);
            this.modelChange.emit();
            // Keep 100% shortly so user can perceive completion.
            setTimeout(() => {
              this.setThumbnailUploadState(thumbnailUploadKey, false);
              this.cleanupThumbnailUpload(thumbnailUploadKey);
              this.modelChange.emit();
              resolve();
            }, 350);
          }
        },
        error => {
          this.stopThumbnailProgressSimulation(thumbnailUploadKey);
          this.setThumbnailUploadState(thumbnailUploadKey, false);
          this.cleanupThumbnailUpload(thumbnailUploadKey);
          console.error(`Thumbnail generation failed for ${file.name}:`, error);
          // Continue upload, but mark for retry after upload completes.
          // If upload already finished, retry from URL immediately to avoid race-condition.
          const mediaIndex = this.findMediaIndexByClientKey(mediaItem);
          if (mediaIndex !== -1) {
            const currentMedia = this.subsection.media[mediaIndex];
            const mediaUrl = currentMedia && currentMedia.url ? currentMedia.url : null;
            this.subsection.media[mediaIndex] = {
              ...currentMedia,
              needsThumbnailRetry: true,
              thumbnailUploadKey: null,
              thumbnailProcessing: true,
            };
            this.subsection.media = [...this.subsection.media];
            this.modelChange.emit();

            if (mediaUrl && !this.isBlobUrl(mediaUrl)) {
              this.videoThumbnailService.generateThumbnailFromVideoUrl(mediaUrl, "digital/thumb", 5).subscribe(
                (fromUrl: any) => {
                  if (!fromUrl || fromUrl.type !== "complete" || !fromUrl.url) {
                    return;
                  }
                  const latestIndex = this.subsection.media.findIndex(
                    (m: any) =>
                      m.mediaClientKey === mediaItem.mediaClientKey ||
                      (m.fileName === mediaItem.fileName && m.url === mediaUrl)
                  );
                  if (latestIndex !== -1) {
                    this.subsection.media[latestIndex] = {
                      ...this.subsection.media[latestIndex],
                      thumbnail: fromUrl.url,
                      needsThumbnailRetry: false,
                    };
                    this.subsection.media = [...this.subsection.media];
                    this.modelChange.emit();
                  }
                },
                () => {
                  // keep needsThumbnailRetry=true for future retry paths
                }
              );
            }
          }
          resolve();
        }
      );
    });
  }

  /**
   * Retry thumbnail generation after upload completes.
   * This helps recover cases where initial extraction failed in background tab.
   */
  private async retryGenerateThumbnailAfterUpload(file: File, mediaIndex: number, videoUrl?: string): Promise<void> {
    if (!this.subsection || !this.subsection.media || mediaIndex < 0 || mediaIndex >= this.subsection.media.length) {
      return;
    }

    const media = this.subsection.media[mediaIndex];
    if (!media || media.needsThumbnailRetry !== true) {
      return;
    }

    return new Promise<void>(resolve => {
      /** Never match `!m.url` + fileName only — duplicate names would steal another row's thumbnail. */
      const resolveTargetIndex = (): number => {
        const urlMatch = videoUrl || media.url;
        if (media.mediaClientKey) {
          const byKey = this.subsection.media.findIndex((m: any) => m.mediaClientKey === media.mediaClientKey);
          if (byKey !== -1) {
            return byKey;
          }
        }
        if (urlMatch) {
          const byUrl = this.subsection.media.findIndex(
            (m: any) => m && m.url === urlMatch && (!media.fileName || m.fileName === media.fileName)
          );
          if (byUrl !== -1) {
            return byUrl;
          }
        }
        return mediaIndex >= 0 && mediaIndex < this.subsection.media.length ? mediaIndex : -1;
      };

      const applyThumbnail = (thumbnailUrl: string): void => {
        const targetIndex = resolveTargetIndex();
        if (targetIndex === -1 || !this.subsection.media[targetIndex]) {
          resolve();
          return;
        }
        this.subsection.media[targetIndex] = {
          ...this.subsection.media[targetIndex],
          thumbnail: thumbnailUrl,
          needsThumbnailRetry: false,
          thumbnailLoading: true,
          thumbnailProcessing: true,
        };
        this.subsection.media = [...this.subsection.media];
        this.modelChange.emit();
        resolve();
      };

      const markRetryFlag = (): void => {
        const targetIndex = resolveTargetIndex();
        if (targetIndex !== -1 && this.subsection.media[targetIndex]) {
          this.subsection.media[targetIndex] = {
            ...this.subsection.media[targetIndex],
            needsThumbnailRetry: true,
            // Retry path finished but still failed; stop processing state to avoid endless spinner.
            thumbnailProcessing: false,
          };
          this.subsection.media = [...this.subsection.media];
          this.modelChange.emit();
        }
        resolve();
      };

      const fallbackFromVideoUrl = (): void => {
        if (!videoUrl) {
          markRetryFlag();
          return;
        }
        this.videoThumbnailService.generateThumbnailFromVideoUrl(videoUrl, "digital/thumb", 5).subscribe(
          (fromUrl: any) => {
            // Ignore progress events; only resolve when complete arrives.
            if (!fromUrl || fromUrl.type !== "complete") {
              return;
            }
            if (fromUrl.url) {
              applyThumbnail(fromUrl.url);
            } else {
              markRetryFlag();
            }
          },
          () => markRetryFlag()
        );
      };

      this.videoThumbnailService.generateThumbnailFromVideo(file, "digital/thumb", 5).subscribe(
        (response: any) => {
          // Ignore progress events; wait for complete.
          if (!response || response.type !== "complete") {
            return;
          }
          if (response.url) {
            applyThumbnail(response.url);
            return;
          }
          fallbackFromVideoUrl();
        },
        () => fallbackFromVideoUrl()
      );
    });
  }

  /**
   * Final fallback for missing thumbnails after upload completion.
   * Resolves row by `mediaClientKey` + `videoUrl` so sort order cannot point at the wrong item.
   */
  private async ensureThumbnailFromUploadedUrl(
    videoUrl: string,
    fileName?: string,
    mediaClientKey?: string
  ): Promise<void> {
    if (!videoUrl || this.isBlobUrl(videoUrl) || !this.subsection || !this.subsection.media) {
      return;
    }

    const startRow = this.findMediaRowNeedingThumbnailFromUrl(videoUrl, fileName, mediaClientKey);
    if (startRow === -1) {
      return;
    }

    this.ensureMediaClientKeyForRow(this.subsection.media[startRow]);
    const rowClientKey = this.subsection.media[startRow].mediaClientKey;
    const dedupeKey = this.thumbnailFromUrlDedupeKey(videoUrl, rowClientKey, fileName);
    if (this.thumbnailFromUrlInFlight.has(dedupeKey)) {
      return;
    }
    this.thumbnailFromUrlInFlight.add(dedupeKey);

    const finish = (): void => {
      this.thumbnailFromUrlInFlight.delete(dedupeKey);
    };

    return new Promise<void>(resolve => {
      this.videoThumbnailService.generateThumbnailFromVideoUrl(videoUrl, "digital/thumb", 5).subscribe(
        (response: any) => {
          if (!response || response.type !== "complete") {
            return;
          }
          if (!response.url) {
            const failedIdx = this.findMediaRowNeedingThumbnailFromUrl(videoUrl, fileName, rowClientKey);
            if (failedIdx !== -1) {
              this.subsection.media[failedIdx] = {
                ...this.subsection.media[failedIdx],
                thumbnailProcessing: false,
                needsThumbnailRetry: true,
              };
              this.subsection.media = [...this.subsection.media];
              this.modelChange.emit();
            }
            finish();
            resolve();
            return;
          }

          const latestIndex = this.findMediaRowForThumbnailAssign(videoUrl, fileName, rowClientKey);
          if (latestIndex === -1) {
            finish();
            resolve();
            return;
          }

          const row = this.subsection.media[latestIndex];
          if (row.thumbnail && String(row.thumbnail).trim() !== "") {
            finish();
            resolve();
            return;
          }

          this.subsection.media[latestIndex] = {
            ...this.subsection.media[latestIndex],
            thumbnail: response.url,
            needsThumbnailRetry: false,
            thumbnailLoading: true,
            thumbnailProcessing: true,
          };
          this.subsection.media = [...this.subsection.media];
          this.modelChange.emit();
          persistExistingVideoThumbnail(
            this.travelKitService,
            this.subsection.media[latestIndex],
            this.subsection && this.subsection.id
          );
          finish();
          resolve();
        },
        () => {
          const failedIndex = this.findMediaRowNeedingThumbnailFromUrl(videoUrl, fileName, rowClientKey);
          if (failedIndex !== -1) {
            this.subsection.media[failedIndex] = {
              ...this.subsection.media[failedIndex],
              thumbnailProcessing: false,
              needsThumbnailRetry: true,
            };
            this.subsection.media = [...this.subsection.media];
            this.modelChange.emit();
          }
          finish();
          resolve();
        }
      );
    });
  }

  /**
   * Get video upload text
   */
  getVideoUploadText(): string {
    if (!this.subsection.media || this.subsection.media.length === 0) {
      return "Select Videos";
    }
    const count = this.subsection.media.length;
    return count === 1 ? "1 video selected" : `${count} videos selected`;
  }

  /**
   * Remove video
   */
  removeVideo(mediaIndex: number): void {
    // Cancel upload if in progress
    const media = this.subsection.media[mediaIndex];
    const mediaId = Number(media && media.id);
    if (Number.isFinite(mediaId) && mediaId > 0) {
      // Emit delete event for media with id
      this.deleteMediaEvent.emit(mediaId);
    }
    if (media && media.uploadKey) {
      // Cancel upload if in progress
      this.cancelUpload(media.uploadKey);
      this.removeUploadState(media.uploadKey);
      this.uploadStates.delete(media.uploadKey);
      this.uploadProgressMap.delete(media.uploadKey);
    }
    this.subsection.media.splice(mediaIndex, 1);
    this.modelChange.emit();
  }

  /**
   * Delete all videos
   */
  deleteAllVideos(): void {
    if (!this.subsection.media || this.subsection.media.length === 0) {
      this.toastr.info("No videos to delete");
      return;
    }

    const videoCount = this.subsection.media.length;

    Swal.fire({
      title: "Are you sure?",
      text: `This will delete all ${videoCount} video(s) from this sub-section.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        // Emit delete events for media with ids
        const mediaIdsToDelete = this.subsection.media
          .map((media: any) => Number(media && media.id))
          .filter((id: number) => Number.isFinite(id) && id > 0);

        if (mediaIdsToDelete.length > 0) {
          mediaIdsToDelete.forEach((id: number) => this.deleteMediaEvent.emit(id));
        }

        // Cancel all uploads
        this.subsection.media.forEach((media: any) => {
          if (media.uploadKey) {
            this.cancelUpload(media.uploadKey);
            this.removeUploadState(media.uploadKey);
            this.uploadStates.delete(media.uploadKey);
            this.uploadProgressMap.delete(media.uploadKey);
          }
        });

        this.subsection.media = [];
        this.toastr.success(`All ${videoCount} video(s) deleted successfully`);
        this.modelChange.emit();
      }
    });
  }

  /**
   * Resume upload for a media item
   */
  /**
   * Retry complete multipart upload (when all parts are uploaded but complete failed)
   */
  async retryCompleteUpload(media: any): Promise<void> {
    if (!media.uploadKey) {
      this.toastr.error("Cannot retry: upload key not found");
      return;
    }

    if (!media.canRetryComplete) {
      this.toastr.error("Cannot retry: this upload cannot be retried");
      return;
    }

    const fileId = media.uploadKey;
    const state = this.uploadStates.get(fileId) || this.loadUploadState(fileId);

    if (!state) {
      this.toastr.error("Cannot retry: upload state not found");
      return;
    }

    // Validate that all parts are uploaded
    if (state.uploadedParts.length !== state.partsCount) {
      this.toastr.error("Cannot retry: not all parts are uploaded. Please resume upload first.");
      return;
    }

    try {
      this.toastr.info("Retrying complete upload...", "Retry");
      this.updateProgress(fileId, 95, "uploading"); // Show progress

      // Clear error flag
      const mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
      if (mediaIndex > -1) {
        this.subsection.media[mediaIndex] = {
          ...this.subsection.media[mediaIndex],
          uploadError: null,
          canRetryComplete: false,
        };
      }

      const finalUrl = await this.multipartS3Upload.completeMultipart(state.key, state.uploadId, state.uploadedParts);

      // Success - update media item
      if (state.mediaItem) {
        const mediaIndex = this.subsection.media.findIndex((m: any) => m.tempId === state.mediaItem.tempId);
        if (mediaIndex > -1) {
          console.log(`✅ Retry complete: Saving URL to media item at index ${mediaIndex}:`, finalUrl);
          this.subsection.media[mediaIndex] = {
            ...this.subsection.media[mediaIndex],
            url: finalUrl, // Ensure URL is saved
            uploadKey: null,
            isPending: false,
            uploadError: null,
            canRetryComplete: false,
          };
          delete this.subsection.media[mediaIndex].tempId;
          console.log(`✅ Media item after retry complete update:`, {
            url: this.subsection.media[mediaIndex].url,
            fileName: this.subsection.media[mediaIndex].fileName,
          });
        } else {
          console.error(`❌ Retry complete: Could not find media item with tempId: ${state.mediaItem.tempId}`);
        }
      } else {
        console.error(`❌ Retry complete: state.mediaItem is null, cannot save URL`);
      }

      // Cleanup
      this.uploadCancelled.delete(fileId);
      this.removeUploadState(fileId);
      this.uploadStates.delete(fileId);
      this.updateProgress(fileId, 100, "completed");
      this.drainAutoThumbnailQueue();

      // Emit completion
      this.uploadComplete.emit({
        file: state.file,
        url: finalUrl,
        fileId,
        mediaItem: state.mediaItem,
      });

      this.toastr.success(`Video ${state.fileName} uploaded successfully`);
      this.modelChange.emit();
    } catch (error) {
      const errorMsg = this.getReadableUploadError(error);
      console.error(`❌ Retry complete failed for ${state.fileName}:`, errorMsg);

      // Update error state
      this.updateProgress(fileId, 0, "error");

      const mediaIndex = this.subsection.media.findIndex((m: any) => m.uploadKey === fileId);
      if (mediaIndex > -1) {
        this.subsection.media[mediaIndex] = {
          ...this.subsection.media[mediaIndex],
          uploadError: errorMsg,
          canRetryComplete: true, // Keep retry option
        };
      }

      this.toastr.error(`Retry failed: ${errorMsg}`, "Upload Error");
      this.modelChange.emit();
    }
  }

  async resumeUploadMedia(media: any): Promise<void> {
    // Find fileId from media (could be uploadKey or need to search by tempId)
    let fileId: string | null = null;

    if (media.uploadKey) {
      fileId = media.uploadKey;
    } else if (media.tempId) {
      // Try to find fileId from uploadStates by matching tempId
      this.uploadStates.forEach((state, id) => {
        if (!fileId && state.mediaItem && state.mediaItem.tempId === media.tempId) {
          fileId = id;
        }
      });
      // If not found in memory, try localStorage
      if (!fileId) {
        const keys = Object.keys(localStorage);
        for (const key of keys) {
          if (key.startsWith(this.STORAGE_PREFIX)) {
            try {
              const state = JSON.parse(localStorage.getItem(key) || "{}");
              if (state.mediaItem && state.mediaItem.tempId === media.tempId) {
                fileId = state.fileId;
                break;
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }
    }

    if (!fileId) {
      this.toastr.error("Cannot resume: upload key not found");
      return;
    }

    const state = this.uploadStates.get(fileId) || this.loadUploadState(fileId);
    if (!state) {
      this.toastr.error("Cannot resume: upload state not found");
      return;
    }

    // Check resume count limit (max 3 attempts)
    const currentResumeCount = state.resumeCount || 0;
    if (currentResumeCount >= 3) {
      const errorMsg = "Maximum resume attempts (3) reached. Please start a new upload.";
      this.toastr.error(errorMsg, "Resume Limit Reached");

      // Update media item to show error
      const mediaIndex = this.subsection.media.findIndex(
        (m: any) => m.tempId === media.tempId || m.uploadKey === fileId
      );
      if (mediaIndex > -1) {
        this.subsection.media[mediaIndex] = {
          ...this.subsection.media[mediaIndex],
          uploadKey: null,
          uploadError: errorMsg,
          canResume: false,
        };
        this.modelChange.emit();
      }
      return;
    }

    // Try to resume with existing file if available
    if (state.file) {
      try {
        // Clear error state before resuming
        const mediaIndex = this.subsection.media.findIndex(
          (m: any) => m.tempId === media.tempId || m.uploadKey === fileId
        );
        if (mediaIndex > -1) {
          this.subsection.media[mediaIndex] = {
            ...this.subsection.media[mediaIndex],
            uploadError: null,
            canResume: true,
            uploadKey: fileId, // Restore uploadKey to show progress
            resumeCount: state.resumeCount || 0, // Keep resume count
          };
          this.modelChange.emit();
        }

        // Clear cancelled flag before resuming
        this.uploadCancelled.delete(fileId);

        await this.uploadSingleFile(state.file, fileId, true);
      } catch (error) {
        this.toastr.error(`Failed to resume upload: ${this.getReadableUploadError(error)}`);
      }
    } else {
      // File reference lost, need user to select file again
      this.toastr.warning("File reference lost. Please re-select the file to resume upload.");
      // Could implement file input dialog here
      // For now, we'll try to find file from uploadStates
      const currentState = this.uploadStates.get(fileId);
      if (currentState && currentState.file) {
        try {
          // Clear error state before resuming
          const mediaIndex = this.subsection.media.findIndex(
            (m: any) => m.tempId === media.tempId || m.uploadKey === fileId
          );
          if (mediaIndex > -1) {
            this.subsection.media[mediaIndex] = {
              ...this.subsection.media[mediaIndex],
              uploadError: null,
              canResume: true,
              uploadKey: fileId,
              resumeCount: currentState.resumeCount || 0,
            };
            this.modelChange.emit();
          }

          // Clear cancelled flag before resuming
          this.uploadCancelled.delete(fileId);

          await this.uploadSingleFile(currentState.file, fileId, true);
        } catch (error) {
          this.toastr.error(`Failed to resume upload: ${this.getReadableUploadError(error)}`);
        }
      }
    }
  }

  /**
   * Get progress percent
   */
  getProgressPercent(fileId: string): number {
    const progress = this.uploadProgressMap.get(fileId);
    return progress ? progress.percent || 0 : 0;
  }

  /**
   * Check if upload is paused
   */
  isPaused(fileId: string): boolean {
    const progress = this.uploadProgressMap.get(fileId);
    return progress ? progress.status === "paused" || progress.status === "error" : false;
  }

  /**
   * Preview click handler
   */
  onPreviewClick(media: any, type: string): void {
    if (media.url && !this.isBlobUrl(media.url)) {
      window.open(media.url, "_blank");
    }
  }

  /**
   * Check if URL is a blob URL
   */
  isBlobUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return url.startsWith("blob:");
  }

  /**
   * Show native video controls when a source URL exists (S3 or blob preview).
   */
  hasPlayableVideoUrl(media: any): boolean {
    if (!media || !media.url) {
      return false;
    }
    const u = String(media.url).trim();
    return u.length > 0;
  }

  isThumbnailUploadingNow(media: any): boolean {
    return !!(media && media.thumbnailUploadKey && this.isThumbnailUploadInProgress(media.thumbnailUploadKey));
  }

  isWaitingForS3ThumbnailSwap(media: any): boolean {
    return !!(media && media.thumbnailS3Pending);
  }

  /**
   * Avoid showing black video frame while thumbnail is still uploading/loading.
   */
  shouldShowInlineVideoPlayer(media: any): boolean {
    return (
      this.hasPlayableVideoUrl(media) &&
      !this.isThumbnailUploadingNow(media) &&
      !this.isWaitingForS3ThumbnailSwap(media) &&
      !media.thumbnailLoading &&
      !media.thumbnailProcessing
    );
  }

  /**
   * Cleanup when component is destroyed - cancel all active uploads
   */
  ngOnDestroy(): void {
    this.isDestroying = true;

    if (this.visibilityChangeHandler) {
      document.removeEventListener("visibilitychange", this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    if (this.windowFocusHandler) {
      window.removeEventListener("focus", this.windowFocusHandler);
      this.windowFocusHandler = null;
    }
    this.thumbnailFromUrlInFlight.clear();

    // Mark all uploads as cancelled first (this will stop new API calls)
    this.uploadStates.forEach((state, fileId) => {
      this.uploadCancelled.set(fileId, true);
    });

    this.uploadStreamSubs.forEach(sub => {
      try {
        sub.unsubscribe();
      } catch (_e) {
        /* ignore */
      }
    });
    this.uploadStreamSubs.clear();

    // Mark all uploads as cancelled
    this.uploadStates.forEach((state, fileId) => {
      this.uploadCancelled.set(fileId, true);

      // Remove pending media items that are still uploading
      if (this.subsection && this.subsection.media) {
        const mediaIndex = this.subsection.media.findIndex(
          (m: any) => m.uploadKey === fileId || (m.isPending === true && !m.url)
        );
        if (mediaIndex > -1) {
          const mediaItem = this.subsection.media[mediaIndex];
          // Only remove if it's still pending (no URL yet)
          if (!mediaItem.url || mediaItem.isPending) {
            this.subsection.media.splice(mediaIndex, 1);
          }
        }
      }
    });

    // Cleanup all upload states
    this.uploadStates.clear();
    this.uploadProgressMap.clear();
    this.uploadCancelled.clear();
    this.pendingAutoThumbnailJobs = [];
    this.runningAutoThumbnailJobs = 0;
    Object.keys(this.thumbnailProgressTimers).forEach(key => this.stopThumbnailProgressSimulation(key));
    this.thumbnailUploadProgress = {};
    this.thumbnailUploadStates = {};
  }

  /**
   * Handle video error
   */
  videoSrc(media: any): string {
    if (!media || !media.url) return "";
    return String(media.url);
  }

  onVideoError(event: any, media: any): void {
    if (this.isDestroying) {
      return;
    }
    console.error(`Video load error for ${media.fileName}:`, event);
    if (this.isBlobUrl(media.url)) {
      console.warn(`Blob URL error detected, clearing video src for ${media.fileName}`);
    }
  }

  /**
   * Track by index
   */
  trackByIndex(index: number, item: any): any {
    return index;
  }

  /**
   * Stable row identity so Angular does not reuse another card's &lt;img&gt; after sort (blank/wrong preview).
   */
  trackByMediaKey(_index: number, item: any): string {
    if (!item) {
      return `i_${_index}`;
    }
    return (
      item.mediaClientKey ||
      (item.id != null && String(item.id)) ||
      item.tempId ||
      item.uploadKey ||
      `${item.fileName || "video"}_${item.url || _index}`
    );
  }

  hasDisplayableThumbnail(media: any): boolean {
    if (!media) return false;
    // Local preview (base64 during upload/migration) is always displayable
    if (media.thumbnailLocalPreview && String(media.thumbnailLocalPreview).trim().length > 0) return true;
    // Permanent thumbnail must be a real URL, not a data: URL
    const t = media.thumbnail;
    if (typeof t !== "string") return false;
    const s = t.trim();
    return s.length > 0 && s !== "data:," && !s.startsWith("data:");
  }

  canRetryThumbnail(media: any): boolean {
    if (!media || this.isDestroying) {
      return false;
    }
    if (media.isPending === true) {
      return false;
    }
    if (media.uploadKey) {
      return false;
    }
    if (media.uploadError || media.thumbnailProcessing) {
      return false;
    }
    if (this.isThumbnailUploadingNow(media) || this.isWaitingForS3ThumbnailSwap(media)) {
      return false;
    }
    if (this.hasDisplayableThumbnail(media)) {
      return false;
    }
    if (!this.hasPlayableVideoUrl(media) || this.isBlobUrl(media.url)) {
      return false;
    }
    return media.needsThumbnailRetry === true;
  }

  getThumbnailProcessingMessage(media: any): string {
    if (!media) {
      return "Processing thumbnail...";
    }
    if (media.needsThumbnailRetry && media.thumbnailProcessing) {
      return "Retrying thumbnail...";
    }
    return "Processing thumbnail...";
  }

  async retryThumbnailForMedia(media: any): Promise<void> {
    if (!this.canRetryThumbnail(media)) {
      return;
    }
    this.ensureMediaClientKeyForRow(media);
    media.thumbnailProcessing = true;
    media.needsThumbnailRetry = true;
    media.thumbnailLoading = false;
    this.subsection.media = [...this.subsection.media];
    this.modelChange.emit();

    try {
      await this.ensureThumbnailFromUploadedUrl(media.url, media.fileName, media.mediaClientKey);
    } finally {
      const target = this.subsection.media.find((m: any) => m && m.mediaClientKey === media.mediaClientKey) || media;
      if (!this.hasDisplayableThumbnail(target)) {
        target.thumbnailProcessing = false;
      }
      this.subsection.media = [...this.subsection.media];
      this.modelChange.emit();
    }
  }

  thumbnailImageSrc(media: any): string {
    if (!this.hasDisplayableThumbnail(media)) {
      return "";
    }
    // Prefer local preview (base64) while S3 upload is in progress
    if (media.thumbnailLocalPreview && String(media.thumbnailLocalPreview).trim().length > 0) {
      return String(media.thumbnailLocalPreview).trim();
    }
    const s = String(media.thumbnail).trim();
    const b = media.thumbnailSrcBust;
    if (b == null || b === "") {
      return s;
    }
    const sep = s.includes("?") ? "&" : "?";
    return `${s}${sep}thumbcb=${encodeURIComponent(String(b))}`;
  }

  onThumbnailImageLoad(media: any): void {
    if (!media) return;
    media.thumbnailLoading = false;
    media.thumbnailProcessing = false;
    delete media.thumbnailSrcBust;
    if (media.thumbnailS3Pending && media.thumbnail === media.thumbnailS3Pending) {
      delete media.thumbnailS3Pending;
      delete media.thumbnailLocalPreview;
    }
    this.modelChange.emit();
  }

  onThumbnailImageError(media: any, event?: any): void {
    if (!media || this.isDestroying) return;

    media.thumbnailLoading = false;

    const hadThumb = typeof media.thumbnail === "string" && media.thumbnail.trim() !== "";
    const videoUrl = media.url && !this.isBlobUrl(media.url) ? media.url : null;

    if (hadThumb) {
      media.thumbnail = null;
      delete media.thumbnailSrcBust;
      if (videoUrl) {
        media.thumbnailProcessing = true;
        void this.ensureThumbnailFromUploadedUrl(videoUrl, media.fileName, media.mediaClientKey);
      } else {
        media.thumbnailProcessing = false;
      }
    } else {
      media.thumbnailProcessing = false;
    }

    this.modelChange.emit();
  }

  async uploadThumbnailForVideo(file: File, media: any, event?: any): Promise<void> {
    const resetInput = () => {
      if (event && event.target) {
        event.target.value = "";
      }
    };

    if (!file || !media) {
      resetInput();
      return;
    }

    const validImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/heic",
      "image/heif",
    ];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    const isValidType =
      validImageTypes.includes(file.type) ||
      (isHeicByExtension && (!file.type || file.type === "application/octet-stream"));
    if (!isValidType) {
      this.toastr.error(`File ${file.name} must be an image (JPEG, PNG, GIF, WebP, or HEIC)`, "Error");
      resetInput();
      return;
    }

    const maxThumbnailSizeMB = 10;
    if (file.size / 1024 / 1024 > maxThumbnailSizeMB) {
      this.toastr.error(`Thumbnail image ${file.name} exceeds ${maxThumbnailSizeMB}MB limit`, "Error");
      resetInput();
      return;
    }

    try {
      file = await this.heicConverter.convertToJpegIfNeeded(file);
      const uploadKey = `thumbnail_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      media.thumbnailProcessing = true;
      media.thumbnailUploadKey = uploadKey;
      this.setThumbnailUploadState(uploadKey, true);

      await this.uploadSingleThumbnail(file, uploadKey, media, "digital/photos", maxThumbnailSizeMB);

      delete media.thumbnailUploadKey;
      this.toastr.success(`Thumbnail for ${media.fileName || "video"} uploaded successfully`);
    } catch (error) {
      if (media.thumbnailUploadKey) {
        delete media.thumbnailUploadKey;
      }
      media.thumbnailProcessing = false;
      this.toastr.error(`Failed to upload thumbnail: ${this.getReadableUploadError(error)}`, "Error");
    }

    resetInput();
  }

  isThumbnailUploadInProgress(key: string): boolean {
    return this.thumbnailUploadStates[key] || false;
  }

  getThumbnailProgressByKey(key: string): number {
    return this.thumbnailUploadProgress[key] || 0;
  }

  private setThumbnailUploadState(key: string, isUploading: boolean): void {
    this.thumbnailUploadStates[key] = isUploading;
  }

  private setThumbnailUploadProgress(key: string, percent: number): void {
    this.thumbnailUploadProgress[key] = percent;
  }

  private cleanupThumbnailUpload(key: string): void {
    this.stopThumbnailProgressSimulation(key);
    delete this.thumbnailUploadStates[key];
    delete this.thumbnailUploadProgress[key];
    delete this.thumbnailProgressStartedAt[key];
  }

  private startThumbnailProgressSimulation(key: string): void {
    this.stopThumbnailProgressSimulation(key);
    this.thumbnailProgressStartedAt[key] = Date.now();
    this.thumbnailProgressTimers[key] = setInterval(() => {
      if (!this.thumbnailUploadStates[key]) {
        this.stopThumbnailProgressSimulation(key);
        return;
      }

      const current = this.thumbnailUploadProgress[key] || 10;
      const startedAt = this.thumbnailProgressStartedAt[key] || Date.now();
      const elapsedSec = (Date.now() - startedAt) / 1000;

      // Time-based target curve for more realistic progress:
      // fast early, slower near the end, and never too close to 100 until complete.
      let target = 10;
      if (elapsedSec <= 8) {
        target = 10 + elapsedSec * 5; // 10 -> 50
      } else if (elapsedSec <= 20) {
        target = 50 + (elapsedSec - 8) * 2.5; // 50 -> 80
      } else if (elapsedSec <= 40) {
        target = 80 + (elapsedSec - 20) * 0.7; // 80 -> 94
      } else {
        target = 94 + (elapsedSec - 40) * 0.1; // very slow tail
      }
      target = Math.min(97, target);

      if (current < target) {
        const next = Math.min(target, current + 2);
        this.thumbnailUploadProgress[key] = Math.round(next);
        this.modelChange.emit();
      }
    }, 300);
  }

  private stopThumbnailProgressSimulation(key: string): void {
    const timer = this.thumbnailProgressTimers[key];
    if (timer) {
      clearInterval(timer);
      delete this.thumbnailProgressTimers[key];
    }
  }

  private uploadSingleThumbnail(
    file: File,
    uploadKey: string,
    mediaItem: any,
    folder: string,
    maxSizeMB: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.uploadService.uploadFileToS3(folder, file, maxSizeMB).subscribe({
        next: (response: any) => {
          if (response && response.type === "progress") {
            this.setThumbnailUploadProgress(uploadKey, response.percent);
            return;
          }

          if (response && response.type === "complete") {
            this.setThumbnailUploadState(uploadKey, false);
            mediaItem.thumbnail = response.url;
            mediaItem.thumbnailLoading = true;
            mediaItem.thumbnailProcessing = true;
            if (mediaItem.thumbnailUploadKey === uploadKey) {
              delete mediaItem.thumbnailUploadKey;
            }
            this.modelChange.emit();
            this.cleanupThumbnailUpload(uploadKey);
            resolve(response);
          }
        },
        error: error => {
          this.setThumbnailUploadState(uploadKey, false);
          if (mediaItem.thumbnailUploadKey === uploadKey) {
            delete mediaItem.thumbnailUploadKey;
          }
          mediaItem.thumbnailProcessing = false;
          this.cleanupThumbnailUpload(uploadKey);
          reject(error);
        },
      });
    });
  }

  getUploadInputId(): string {
    return `${this.uploadIdPrefix}-${this.sectionIndex}-${this.subIndex}`;
  }

  getUploadFileNameId(): string {
    return `${this.uploadIdPrefix}-name-${this.sectionIndex}-${this.subIndex}`;
  }

  /**
   * Get total video count in section
   */
  private getTotalVideoCountInSection(): number {
    if (typeof this.sectionVideoCount === "number") {
      return this.sectionVideoCount;
    }

    if (!this.subsection || !this.subsection.media) {
      return 0;
    }
    return this.subsection.media.length;
  }

  /**
   * Format file size
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  private getReadableUploadError(error: any): string {
    if (!error) return "Unknown upload error";
    if (typeof error.message === "string" && error.message.trim() !== "") {
      const msg = error.message.trim();
      if (msg.includes("function ()") || msg.startsWith("Failed to initiate upload:")) {
        return "Network error";
      }
      return msg;
    }
    if (typeof error.statusText === "string" && error.statusText.trim() !== "") {
      return error.statusText;
    }
    if (typeof error.type === "string" && error.type.trim() !== "") {
      return error.type;
    }
    if (error instanceof ProgressEvent) {
      return "Network error";
    }
    return "Upload failed";
  }
}
